import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { useEffect } from "react";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useSetIsPrimaryNavigationStripHidden } from "@-/dev.improve.haniverse.routes.nav._context";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { SlideLessonPageContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._context";

/** Configures the slide footer and primary navigation strip. The primary controls strip is always visible. */
export function SlideOptions({
  isPrimaryNavigationStripVisible = true,
  nextButtonLabel = "Next Card",
}: {
  isPrimaryNavigationStripVisible?: boolean;
  nextButtonLabel?: string;
}) {
  const [setNextButtonLabelOption, isActive] = useContextSelectors(
    SlideLessonPageContext,
    (c) => c.setNextButtonLabelOption,
    (c) => c.isActive,
  );
  const { hanziScript } = useActorUser();
  const setIsPrimaryNavigationStripHidden = useSetIsPrimaryNavigationStripHidden();
  // The slide screen stays mounted while other tabs are focused, so unmount cleanup alone can't
  // restore the chrome — the override would stay stuck hidden on every other tab. Gate on focus
  // so the hides apply only while the slide is actually on screen and wear off on blur.
  const isFocused = useIsFocused();

  // Capitalize for display: "simplified" -> "Simplified", "traditional" -> "Traditional".
  const hanziScriptLabel = hanziScript.charAt(0).toUpperCase() + hanziScript.slice(1);
  const resolvedNextButtonLabel = nextButtonLabel.replaceAll("${hanziScript}", hanziScriptLabel);

  useEffect(() => {
    setNextButtonLabelOption(resolvedNextButtonLabel);

    return () => {
      setNextButtonLabelOption(null);
    };
  }, [resolvedNextButtonLabel, setNextButtonLabelOption]);

  // Session-only nav-context overrides: drive them from the slide's preference while the slide is
  // FOCUSED, and restore the chrome when it blurs or unmounts. The slide screen stays mounted when
  // you switch tabs, so unmount cleanup alone would leave the tab bar / toggle stuck hidden on the
  // other tabs — gating on focus makes the hides "wear off" the moment the slide loses focus.
  useEffect(() => {
    if (!isFocused || !isActive) {
      return;
    }
    setIsPrimaryNavigationStripHidden(!isPrimaryNavigationStripVisible);
    return () => {
      setIsPrimaryNavigationStripHidden(false);
    };
  }, [isActive, isPrimaryNavigationStripVisible, isFocused, setIsPrimaryNavigationStripHidden]);
  return null;
}
